import React, { FC, useEffect, useState } from "react";
import {
    Button,
    Card,
    Collapse,
    FormControl,
    InputGroup,
    Modal
} from "react-bootstrap";
import { Project, ProjectSettings, Revision } from "../../models/types";
import { SlackInfoMessage } from "../simple/SlackInfoMessage";
import { ProjectConfig } from "../views/settings/ProjectConfig";
import { useLocalState } from "../../graphql/hooks";
import { getEnvironmentTag } from "../../common/Helpers";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { CopyButton } from "../buttons/CopyButton";

interface Props {
    project: Project;
    revision?: Revision;
    show: boolean;
    onSave: (settings: ProjectSettings) => void;
    onClose: () => void;
}

export const CuttlefishProjectSettingsModal: FC<Props> = ({
    show,
    onSave,
    onClose,
    project,
    revision
}) => {
    const [showBasicDetails, updateShowBasicDetails] = useState(false);
    const [name, updateName] = useState(project.name);
    const [channel, updateChannel] = useState(project.slackChannel);
    const [id, updateId] = useState(project.gitProjectId);
    const [gitProjectName, updateGitProjectName] = useState(
        project.gitProjectName
    );
    const [gitProjectBranch, updateBranch] = useState(project.gitProjectBranch);
    const { useDarkMode } = useLocalState();

    useEffect(() => {
        updateName(project.name);
        updateId(project.gitProjectId);
        updateGitProjectName(project.gitProjectName);
        updateBranch(project.gitProjectBranch);
        updateChannel(project.slackChannel);
    }, [project]);

    const gitLink =
        "https://git.seepiagames.com/playableads/" + project.gitProjectName;
    const sshClone =
        "git clone --recursive -b " +
        project.gitProjectBranch +
        " ssh://git@git.seepiagames.com:2222/playableads/" +
        project.gitProjectName +
        ".git";
    const cliCommand = revision
        ? `seepia-cli ${getEnvironmentTag()}-i ${revision.id}`
        : undefined;

    return (
        <Modal
            size="xl"
            show={show}
            onHide={onClose}
            backdrop="static"
            keyboard={false}
            className={useDarkMode ? "modal-dark" : ""}
        >
            <Modal.Header closeButton>
                <Modal.Title>{project.name} settings</Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-4">
                <InputGroup className="mb-3">
                    <InputGroup.Text>Project Name</InputGroup.Text>
                    <FormControl
                        defaultValue={name}
                        onChange={(event: any) => {
                            updateName(event.target.value);
                        }}
                    />
                </InputGroup>
                <InputGroup className="mb-3">
                    <InputGroup.Text>Git Project Id</InputGroup.Text>
                    <FormControl
                        defaultValue={id?.toString()}
                        onChange={(event: any) => {
                            updateId(Number(event.target.value));
                        }}
                    />
                </InputGroup>
                <InputGroup className="mb-3">
                    <InputGroup.Text>Git Project Name</InputGroup.Text>
                    <FormControl
                        defaultValue={gitProjectName}
                        onChange={(event: any) => {
                            updateGitProjectName(event.target.value);
                        }}
                    />
                </InputGroup>
                <InputGroup className="mb-3">
                    <InputGroup.Text>Git Project Branch</InputGroup.Text>
                    <FormControl
                        defaultValue={gitProjectBranch}
                        onChange={(event: any) => {
                            updateBranch(event.target.value);
                        }}
                    />
                </InputGroup>
                <InputGroup className="mb-3">
                    <InputGroup.Text>Slack Channel</InputGroup.Text>
                    <FormControl
                        defaultValue={channel}
                        onChange={(event: any) => {
                            updateChannel(event.target.value);
                        }}
                    />
                </InputGroup>
                <SlackInfoMessage />
                <Button
                    className="my-2"
                    onClick={() =>
                        onSave({
                            projectId: project.id,
                            name: name,
                            gitProjectId: id,
                            slackChannel: channel,
                            gitProjectName: gitProjectName,
                            gitProjectBranch: gitProjectBranch
                        })
                    }
                >
                    Save
                </Button>
                <ProjectConfig project={project} />
                <Card className="mt-2 mb-2">
                    <Card.Header
                        onClick={() => {
                            updateShowBasicDetails(!showBasicDetails);
                        }}
                    >
                        Basic info
                        <FontAwesomeIcon
                            size="sm"
                            icon={
                                showBasicDetails ? faChevronUp : faChevronDown
                            }
                            className="mouseHover mx-2"
                            onClick={() => {
                                updateShowBasicDetails(!showBasicDetails);
                            }}
                            title={
                                showBasicDetails
                                    ? "Hide details"
                                    : "Show details"
                            }
                        />
                    </Card.Header>
                    <Collapse in={showBasicDetails}>
                        <div>
                            <Card.Body>
                                <Card.Title as="h6">Product name</Card.Title>
                                <Card.Text>{project.product?.name}</Card.Text>
                                <Card.Title as="h6">Product id</Card.Title>
                                <Card.Text>{project.product?.id}</Card.Text>
                                <Card.Title as="h6">Project name</Card.Title>
                                <Card.Text>{project.name}</Card.Text>
                                <Card.Title as="h6">Project id</Card.Title>
                                <Card.Text>{project.id}</Card.Text>
                                <Card.Title as="h6">Revision name</Card.Title>
                                {revision ? (
                                    <>
                                        <Card.Text>{revision.name}</Card.Text>
                                        <Card.Title as="h6">
                                            Revision id
                                        </Card.Title>
                                        <Card.Text>{revision.id}</Card.Text>
                                    </>
                                ) : null}
                                <Card.Title as="h6">
                                    Git project name
                                </Card.Title>
                                <Card.Text>{project.gitProjectName}</Card.Text>
                                <Card.Title as="h6">Git project id</Card.Title>
                                <Card.Text>{project.gitProjectId}</Card.Text>
                                <Card.Title as="h6">
                                    Git project branch
                                </Card.Title>
                                <Card.Text>
                                    {project.gitProjectBranch}
                                </Card.Text>
                                <Card.Title as="h6">
                                    Source repository
                                </Card.Title>
                                <Card.Text>
                                    <a
                                        href={gitLink}
                                        target="_blank noopener noreffer"
                                    >
                                        {gitLink}
                                    </a>
                                </Card.Text>
                                <Card className="mb-2 mx-2">
                                    <Card.Header>
                                        Git clone url
                                        <CopyButton value={sshClone} />
                                    </Card.Header>
                                    <Card.Body>
                                        <code>{sshClone}</code>
                                    </Card.Body>
                                </Card>
                                {cliCommand ? (
                                    <>
                                        <Card className="mb-2 mx-2">
                                            <Card.Header>
                                                Seepia cli init command
                                                <CopyButton
                                                    value={cliCommand}
                                                />
                                            </Card.Header>
                                            <Card.Body>
                                                <code>{cliCommand}</code>
                                            </Card.Body>
                                        </Card>
                                    </>
                                ) : null}
                            </Card.Body>
                        </div>
                    </Collapse>
                </Card>
            </Modal.Body>
        </Modal>
    );
};
